import React, { useState, useEffect } from 'react';
import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import useMapStore from '@store/useMapStore';
import useMetadataStore from '@store/useMetadataStore';
import { ClickableType, ClickContext, Team } from '@utils/enums';
import { getAvailableActions } from '@api/actionApi';
import { enterTargetingMode, executeSelfAoeAction } from '@services/actionService';
import { getModifierBreakdown, getEffectiveStat } from '@utils/statCalculations';
import { getActionDisplayInfo, calculateActionDamage, getAoeInfo, STAT_ICONS } from '@utils/statIcons';
import calculateAoeTiles, { getUnitsInAoe } from '@helpers/game/calculateAoeTiles';
import { getLastUnitPosition } from '@helpers/game/calculateActionOrigins';
import { Swords, Brain, Heart, Shield, Footprints, HelpCircle, Eye, Rabbit, Zap, TrendingUp, TrendingDown, Crosshair, AlertTriangle, Activity, Sparkle } from 'lucide-react';
import APDisplay from '@components/ui/game/ap-display/APDisplay';
import EnergyDisplay from '@components/ui/game/energy-display/EnergyDisplay';
import CooldownBlocks from '@components/ui/game/cooldown-blocks';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import './ActionsPanel.css';

/**
 * Get the reason why an action is disabled
 * @returns {string|null} - Reason string if disabled, null if usable
 */
const getDisabledReason = (action, canAfford, isPlanPhase, isOwnUnit, isInDeployment, hasForcedActions) => {
  if (!isPlanPhase) return 'Only available during Plan phase';
  if (isInDeployment) return 'Unit must be deployed first';
  if (!isOwnUnit) return 'Cannot control enemy units';
  if (hasForcedActions) return 'Unit controlled by Taunt effect';

  // Check cooldown directly (more reliable than status)
  if (action.cooldown_current > 0) {
    const turns = action.cooldown_current;
    return `On cooldown (${turns} turn${turns > 1 ? 's' : ''} left)`;
  }
  if (!action.has_sufficient_energy) {
    return `Insufficient energy (need ${action.energy_cost})`;
  }
  if (!canAfford) {
    return `Insufficient AP (need ${action.ap_cost})`;
  }
  if (action.status_from_unit_action === 'DISABLED_BY_EFFECT') {
    return 'Disabled by an effect';
  }

  return null;
};

// Icon mapping for dynamic rendering
const iconComponents = {
  Swords,
  Brain,
  Heart,
  Shield,
  Footprints,
  HelpCircle,
  Eye,
  Rabbit,
  Zap,
  TrendingUp,
  TrendingDown,
  Crosshair,
  Activity,
  Sparkle
};

export default function ActionsPanel() {
  // Use selectors for proper reactivity when state changes
  const currentClick = useSelectionStore(state => state.currentClick);
  const clickContext = useSelectionStore(state => state.clickContext);
  const game = useGameStore(state => state.game);
  const isPlanPhase = useGameStore(state => state.isPlanPhase);
  const plannedActions = useGameStore(state => state.getPlannedActions());
  const effects = useMetadataStore(state => state.metadata?.effects);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Only show if a unit is selected
  const showPanel = currentClick && currentClick.type === ClickableType.UNIT;

  // Fetch available actions when unit is selected or turn changes
  // turn_number dependency ensures cooldowns are refreshed after each turn
  useEffect(() => {
    if (!showPanel || !game || !currentClick.data?.id) {
      setAvailableActions([]);
      return;
    }

    const fetchActions = async () => {
      setLoading(true);
      try {
        const actions = await getAvailableActions(game.id, currentClick.data.id);

        if (!actions) {
          throw new Error('[ActionsPanel] No actions returned from API');
        }

        setAvailableActions(actions);
      } catch (error) {
        console.error('[ActionsPanel] Failed to fetch actions:', error);
        setAvailableActions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, [showPanel, game?.id, game?.turn_number, currentClick?.data?.id]);

  if (!showPanel) {
    return null;
  }

  // Get fresh unit data
  const unit = useGameStore.getState().getUnitOrAvailableUnitById(currentClick.data.id);

  if (!unit) {
    console.warn('[ActionsPanel] Unit not found:', currentClick.data.id);
    return null;
  }

  // Check if actions should be disabled
  const isInDeployment = useGameStore.getState().isUnitInDeployment(unit.id);
  const isOwnUnit = unit.teamClass === Team.OWN;

  // Check if unit has forced actions (from Taunt, etc.)
  const hasForcedActions = plannedActions.some(
    pa => pa.unit_id === unit.id && pa.is_forced === true
  );

  const actionsDisabled = !isPlanPhase || isInDeployment || !isOwnUnit || hasForcedActions;

  // Validate unit has required data
  if (!unit.unit_template) {
    throw new Error(`[ActionsPanel] Unit ${unit.id} missing unit_template`);
  }

  if (!unit.unit_template.max_ap) {
    throw new Error(`[ActionsPanel] Unit template missing max_ap for unit ${unit.id}`);
  }

  // Calculate AP with effects like the old implementation
  const baseMaxAp = unit.unit_template.max_ap;
  const maxApBreakdown = getModifierBreakdown(unit, 'max_ap');
  const totalAp = maxApBreakdown.effective;

  // Use current AP but cap it to effective max
  const currentAp = unit.ap;
  const filledAp = Math.min(currentAp, totalAp);

  // Get targeting action to check if we're already targeting a self-AOE
  const targetingAction = useSelectionStore.getState().targetingAction;

  const handleActionClick = (action) => {
    if (!action) {
      throw new Error('[ActionsPanel] Action is null');
    }

    if (!action.slug) {
      throw new Error('[ActionsPanel] Action missing slug');
    }

    // Check if this is a self-AOE action that's already being targeted
    const aoeInfo = getAoeInfo(action);
    const isSelfAoe = aoeInfo.isAoe && aoeInfo.aoeData?.origin === 'self' && action.target_type === 'self';

    if (isSelfAoe && targetingAction?.slug === action.slug) {
      // Second click on same self-AOE action -> execute it
      executeSelfAoeAction(action, unit);
      return;
    }

    // Use enterTargetingMode from actionService
    enterTargetingMode(action, unit);
  };

  // Handler for hovering over self-AOE actions
  const handleActionHover = (action) => {
    if (!action || !unit || !game) return;

    const aoeInfo = getAoeInfo(action);
    const isSelfAoe = aoeInfo.isAoe && aoeInfo.aoeData?.origin === 'self' && action.target_type === 'self';

    if (!isSelfAoe) return;

    // Calculate unit position
    const lastPosition = getLastUnitPosition(game, unit.id);
    const actionOrigin = lastPosition || { x: unit.x, y: unit.y };

    // Calculate AOE tiles
    const mapBounds = { width: game.map.width, height: game.map.height };
    const aoeTiles = calculateAoeTiles(actionOrigin.x, actionOrigin.y, aoeInfo.aoeData, mapBounds);

    // Get units that would be affected
    const units = game.snapshots?.current?.dynamic_objects?.units || game.units || [];
    const affectedUnits = getUnitsInAoe(units, aoeTiles, aoeInfo.aoeData.filters, unit.teamClass);
    const affectedUnitIds = affectedUnits.map(u => u.id);

    // Set AOE preview with beneficial flag
    const { setAoePreview } = useMapStore.getState();
    setAoePreview(aoeTiles, affectedUnitIds, aoeInfo.isBeneficial);
  };

  // Handler for leaving hover on self-AOE actions
  const handleActionLeave = (action) => {
    // Don't clear if action is already in targeting mode (clicked)
    const currentTargeting = useSelectionStore.getState().targetingAction;
    if (currentTargeting?.slug === action?.slug) return;

    const { clearAoePreview } = useMapStore.getState();
    clearAoePreview();
  };

  return (
    <div className="actions-panel">
      <div className="unit-ap-display">
        <div className="ap-breakdown">
          <APDisplay
            baseTotal={baseMaxAp}
            total={totalAp}
            filled={filledAp}
            size="small"
            unit={unit}
            showLabel={true}
          />
        </div>
      </div>

      {/* {loading && (
        <div className="actions-loading">Loading actions...</div>
      )} */}

      {!loading && availableActions.length === 0 && (
        <div className="actions-empty">No actions available</div>
      )}

      {hasForcedActions && (
        <div className="actions-forced-warning">
          <AlertTriangle size={16} />
          <span>Controlled by Taunt - Actions forced</span>
        </div>
      )}

      {/* {actionsDisabled && availableActions.length > 0 && (
        <div className="actions-empty" style={{ color: '#fbbf24' }}>
          {!isPlanPhase ? 'Actions only available in Plan phase' : 'Unit must be deployed first'}
        </div>
      )} */}

      {!loading && availableActions.length > 0 && (
        <div className="actions-list">
          {availableActions.map(action => {
            if (!action) {
              console.warn('[ActionsPanel] Null action in list');
              return null;
            }

            const apCost = action.ap_cost !== undefined ? action.ap_cost : 0;
            const energyCost = action.energy_cost !== undefined ? action.energy_cost : 0;
            const canAfford = filledAp >= apCost;
            const isUsable = action.is_usable && canAfford && !actionsDisabled;
            const isTargeting = clickContext === ClickContext.TARGET_ACTION;

            // Get complete action display info (damage, type, range)
            const displayInfo = getActionDisplayInfo(action);
            const IconComponent = iconComponents[displayInfo.icon] || HelpCircle;

            // Get effect description for buff/debuff actions
            let effectDescription = null;
            if (displayInfo.statChangeInfo?.effectSlug && effects) {
              const effect = effects.find(e => e.slug === displayInfo.statChangeInfo.effectSlug);
              effectDescription = effect?.description;
            }

            // Calculate damage with stat bonuses
            const damageCalc = calculateActionDamage(action, unit, getEffectiveStat);
            const hasScalingStat = damageCalc && damageCalc.scalingStat;
            const ScalingStatIcon = hasScalingStat ? STAT_ICONS[damageCalc.scalingStat] : null;

            // Get reason for being disabled (null if usable)
            const disabledReason = getDisabledReason(action, canAfford, isPlanPhase, isOwnUnit, isInDeployment, hasForcedActions);

            // Check if this action is already planned for this unit
            const isActionPlanned = plannedActions.some(
              pa => pa.unit_id === unit.id && pa.action_id === action.id
            );

            // Get AOE info
            const aoeInfo = getAoeInfo(action);

            // Check if this is a self-AOE action for hover handling
            const isSelfAoe = aoeInfo.isAoe && aoeInfo.aoeData?.origin === 'self' && action.target_type === 'self';
            // Check if this action is currently being targeted (for visual feedback)
            const isActionTargeted = targetingAction?.slug === action.slug;

            const button = (
              <button
                className={`action-button ${!isUsable ? 'disabled' : ''} ${isTargeting ? 'targeting' : ''} ${isActionTargeted ? 'targeted' : ''}`}
                onClick={() => handleActionClick(action)}
                onMouseEnter={isSelfAoe && isUsable ? () => handleActionHover(action) : undefined}
                onMouseLeave={isSelfAoe && isUsable ? () => handleActionLeave(action) : undefined}
                disabled={!isUsable}
                title={action.description}
              >
                {/* Row 1: Name + Costs */}
                <div className="action-info-row">
                  <span className="action-name">{action.name}</span>
                  <div className="action-costs">
                    {apCost > 0 && (
                      <APDisplay
                        baseTotal={apCost}
                        total={apCost}
                        filled={Math.min(apCost, filledAp)}
                        size="tiny"
                        showTooltip={false}
                      />
                    )}
                    {energyCost > 0 && (
                      <EnergyDisplay
                        value={energyCost}
                        size="tiny"
                      />
                    )}
                  </div>
                </div>

                {/* Row 2: Damage/Type + Range + Cooldown */}
                <div className="action-stats-row">
                  <div className="action-stat-info">
                    <IconComponent size={14} className={`action-stat-icon ${displayInfo.damageType ? `damage-type-${displayInfo.damageType}` : ''} ${displayInfo.isHealing ? 'healing-type' : ''}`} />
                    {displayInfo.hasDamage ? (
                      <span className="action-damage-text">
                        {damageCalc ? damageCalc.total : displayInfo.damage}
                        {hasScalingStat && (
                          <span className="action-damage-formula">
                            (Base:{damageCalc.baseDamage} + <ScalingStatIcon size={10} className="action-formula-stat-icon" />{damageCalc.bonus})
                          </span>
                        )}
                      </span>
                    ) : effectDescription ? (
                      <XhelosTooltip content={effectDescription} placement="top">
                        <span className="action-effect-description">{effectDescription}</span>
                      </XhelosTooltip>
                    ) : (
                      <span className="action-type-label">{displayInfo.typeLabel}</span>
                    )}
                  </div>
                  <div className="action-meta">
                    <div className="action-range">
                      <Crosshair size={12} className="action-range-icon" />
                      <span>{displayInfo.rangeDisplay}</span>
                    </div>
                    {aoeInfo.isAoe && (
                      <div className={`action-aoe ${aoeInfo.isBeneficial ? 'aoe-beneficial' : 'aoe-harmful'}`}>
                        <span className="aoe-label">AOE</span>
                        <span className="aoe-range">{aoeInfo.aoeRange}</span>
                      </div>
                    )}
                    <CooldownBlocks
                      cooldownMax={action.cooldown_max}
                      cooldownCurrent={action.cooldown_current}
                      isPlanned={isActionPlanned}
                    />
                  </div>
                </div>
              </button>
            );

            // Wrap with tooltip only when disabled
            if (disabledReason) {
              return (
                <XhelosTooltip key={action.slug} content={disabledReason} placement="left">
                  {button}
                </XhelosTooltip>
              );
            }

            // Clone button with key when no tooltip wrapper
            return React.cloneElement(button, { key: action.slug });
          })}
        </div>
      )}
    </div>
  );
}